import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import getCustomer from '@/shared/api/customers/getCustomer';
import getCustomers from '@/shared/api/customers/getCustomers';
import useDebounce from '@/shared/hooks/useDebounce';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import OrderCustomer from '@/shared/types/order/orderCustomer.type';

import getSearchOverFields from '../utils/getSearchOverFields';
import transformCustomer from '../utils/transformCustomer';

type Args = {
  createdCustomer: OrderCustomer[] | null;
  updatedCustomer: OrderCustomer[] | null;
  shouldResetForm: boolean;
  setShouldResetForm: (value: boolean) => void;
};

const useSearchCustomerOperations = ({
  createdCustomer,
  updatedCustomer,
  shouldResetForm,
  setShouldResetForm,
}: Args) => {
  const redirectLogin = useRedirectLogin();

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const customerId = searchParams.get('customerId');

  const [searchCustomersList, setSearchCustomersList] = useState<
    OrderCustomer[]
  >([]);
  const [selectedCustomer, setSelectedCustomer] = useState<OrderCustomer[]>([]);
  const [selectedCustomerSnapshot, setSelectedCustomerSnapshot] = useState<
    OrderCustomer[]
  >([]);
  const [searchCustomerValue, setSearchCustomerValue] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [refetchCustomers, setRefetchCustomers] = useState(false);
  const [isBillingSameAsDelivery, setIsBillingSameAsDelivery] = useState(false);

  const debouncedSearchCustomerValue = useDebounce(searchCustomerValue, 500);

  useEffect(() => {
    const fetchCustomers = async (searchValue: string) => {
      const searchableFields: (keyof OrderCustomer)[] = [
        'billing_address',
        'billing_address2',
        'billing_address_type',
        'billing_city',
        'billing_country',
        'billing_postcode',
        'billing_state',
        'delivery_address',
        'delivery_address2',
        'delivery_address_type',
        'delivery_city',
        'delivery_country',
        'delivery_postcode',
        'delivery_state',
        'email_address',
        'first_name',
        'last_name',
        'phone_number',
        'in_customer_id',
      ];

      const searchBody = getSearchOverFields<OrderCustomer>(
        searchValue,
        searchableFields,
      );

      const response = await getCustomers(
        { search: searchBody },
        redirectLogin,
      );

      if (response.success) {
        setSearchCustomersList(response.data.map(transformCustomer).flat());
      }
    };

    void fetchCustomers(debouncedSearchCustomerValue);

    if (refetchCustomers) {
      void fetchCustomers(debouncedSearchCustomerValue);
      setRefetchCustomers(false);
    }
  }, [debouncedSearchCustomerValue, refetchCustomers, redirectLogin]);

  useEffect(() => {
    const fetchSelectedCustomer = async (customerId: string) => {
      const response = await getCustomer(customerId, redirectLogin);

      if (response.success) {
        setSelectedCustomer(response.data.map(transformCustomer).flat());
        setSelectedCustomerSnapshot(
          response.data.map(transformCustomer).flat(),
        );
      }
    };

    if (customerId && selectedCustomer.length === 0) {
      void fetchSelectedCustomer(customerId);
    }
  }, [customerId, selectedCustomer, redirectLogin]);

  useEffect(() => {
    if (selectedCustomerId) {
      const customerData = searchCustomersList.filter(
        (customer) => customer.in_customer_id === selectedCustomerId,
      );
      setSelectedCustomer(customerData);
      setSelectedCustomerSnapshot(customerData);
    }
  }, [searchCustomersList, selectedCustomerId]);

  useEffect(() => {
    if (createdCustomer || updatedCustomer) {
      setSelectedCustomer(createdCustomer || updatedCustomer || []);
      setSelectedCustomerSnapshot(createdCustomer || updatedCustomer || []);
    }
  }, [createdCustomer, updatedCustomer]);

  useEffect(() => {
    if (shouldResetForm) {
      setSearchCustomerValue('');
      setSelectedCustomerId('');
      setSelectedCustomer([]);
      setSelectedCustomerSnapshot([]);
      setShouldResetForm(false);
      navigate(location.pathname);
    }
  }, [location.pathname, shouldResetForm, navigate, setShouldResetForm]);

  return {
    searchCustomersList,
    selectedCustomer,
    searchCustomerValue,
    selectedCustomerSnapshot,
    isBillingSameAsDelivery,
    setIsBillingSameAsDelivery,
    setSearchCustomerValue,
    setSelectedCustomerId,
    setSelectedCustomer,
  };
};

export default useSearchCustomerOperations;
